import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  CircleCheck,
  CircleHelp,
  Copy,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Snowflake,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { CategoryDto, MenuDraftDto, ProductDto } from "../../../shared/api-types";
import { TRANSLATION_LANGS, type TranslationLang } from "../../../shared/constants";
import { formatPriceInput, PRICE_ERROR_MESSAGES, parsePriceInput } from "../../../shared/money";
import type { SnapshotProduct } from "../../../shared/snapshot";
import {
  mediaUrl,
  menuApi,
  openPreview,
  useDraft,
  useMenus,
  usePublished,
  usePublishPreview,
} from "../../api/queries";
import { Bar, CHIPS, type MenuItem, PopMenu, StatusChip, Switch } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { currencySymbol, currencyWord, formatWhen, plural, price } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { CategoryDeleteDialog, CategoryDialog, ConfirmDialog, DiscardDialog, PublishDialog } from "./dialogs";
import { formFromProduct, toProductInput } from "./editorForm";
import { ProductEditor } from "./ProductEditor";
import {
  filterBlocks,
  isProductFilter,
  PRODUCT_FILTERS,
  type ProductFilter,
  rowIssues,
  startsCollapsed,
} from "./productFilters";
import { mainMenu, menuChip } from "./status";
import { useRefreshMenus } from "./useRefresh";

type Dialog =
  | { type: "publish" | "discard" }
  | { type: "category"; category?: CategoryDto }
  | { type: "categoryDelete"; category: CategoryDto }
  | { type: "productRemove"; product: ProductDto }
  | null;

type Editor = { product?: ProductDto; categoryId?: string } | null;

/** "Produse și categorii" (prototype page `products`). */
export function ProductsPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const [params, setParams] = useSearchParams();
  const menus = useMenus(rid);
  const navigate = useNavigate();
  const toast = useToast();
  const api = menuApi(rid);
  const refresh = useRefreshMenus(rid);

  const active = (menus.data ?? []).filter((m) => !m.archived);
  const requested = active.find((m) => m.id === params.get("menu"));
  const current = requested ?? mainMenu(menus.data ?? []);
  const mid = current?.id ?? null;
  const draft = useDraft(rid, mid);
  const isPublished = !!current?.publishedVersionId;
  const preview = usePublishPreview(rid, mid, isPublished);
  const published = usePublished(rid, mid, isPublished);

  const [dialog, setDialog] = useState<Dialog>(params.get("publish") === "1" ? { type: "publish" } : null);
  const [editor, setEditor] = useState<Editor>(null);
  const [priceEdit, setPriceEdit] = useState<{ pid: string; value: string; err: string } | null>(null);
  const close = () => setDialog(null);
  // Search, category and filter live in the URL: they survive the editor, a reload and going back (admin review).
  const query = params.get("q") ?? "";
  const catParam = params.get("cat");
  const fParam = params.get("f");
  const filter: ProductFilter = isProductFilter(fParam) ? fParam : "all";
  const setView = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  const setQuery = (v: string) => setView({ q: v || null });
  const [open, setOpen] = useOpenCategories(mid, draft.data);

  const changes = preview.data?.changes ?? [];
  const sets = useMemo(() => {
    const ids = (kind: string) =>
      new Set(changes.filter((c) => c.kind === kind && c.productId).map((c) => c.productId as string));
    return {
      added: ids("add"),
      changed: ids("chg"),
      removed: changes.filter((c) => c.kind === "rem" && c.productId),
      newCategories: new Set(
        changes.filter((c) => c.subject === "Categorie nouă" && c.categoryId).map((c) => c.categoryId),
      ),
    };
  }, [changes]);
  const publishedById = useMemo(
    () => new Map<string, SnapshotProduct>((published.data?.products ?? []).map((p) => [p.id, p])),
    [published.data],
  );

  if (menus.isLoading || (mid && draft.isLoading)) {
    return (
      <p className="muted" role="status">
        Se încarcă meniul…
      </p>
    );
  }
  if (menus.isError || draft.isError) {
    return (
      <Alert tone="error" live>
        {(menus.error ?? draft.error)?.message}
      </Alert>
    );
  }
  if (!current || !draft.data) {
    return (
      <>
        <h1 className="h1">Produse și categorii</h1>
        <div className="empty-card">
          <span className="t">Creează întâi un meniu</span>
          <span className="s">
            Produsele aparțin unui meniu. Importă meniul existent sau pornește de la unul gol.
          </span>
          <div className="row" style={{ justifyContent: "center", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/app/imports")}>
              Importă meniul
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={async () => {
                const m = await api.create("Meniul principal");
                await refresh(m.id);
                setParams({ menu: m.id });
                toast("Meniul principal a fost creat ca ciornă. Adaugă prima categorie.");
              }}
            >
              Creează meniul
            </button>
          </div>
        </div>
      </>
    );
  }

  const d: MenuDraftDto = draft.data;
  const menu = d.menu;
  const cur = d.currency;
  const sym = currencySymbol(cur);
  const st = menu.status;
  const langs = draftLanguages(d);
  const draftChanged = new Set([...sets.added, ...sets.changed]);
  const catId = catParam && d.categories.some((c) => c.id === catParam) ? catParam : null;
  const narrowing = !!query.trim() || filter !== "all";
  const bar =
    st === "pending"
      ? {
          tone: "warning" as const,
          icon: TriangleAlert,
          title: plural(menu.pendingChanges ?? 0, "modificare nepublicată", "modificări nepublicate"),
          sub: `Clienții văd versiunea publicată ${formatWhen(menu.publishedAt)}.`,
        }
      : st === "published"
        ? {
            tone: "success" as const,
            icon: CircleCheck,
            title: "Toate modificările sunt publicate",
            sub: `Ultima publicare: ${formatWhen(menu.publishedAt)}.`,
          }
        : st === "offline"
          ? {
              tone: "neutral" as const,
              icon: EyeOff,
              title: "Retras din public",
              sub: "Clienții nu văd acest meniu. Publică-l ca să reapară la același link.",
            }
          : {
              tone: "info" as const,
              icon: Info,
              title: "Ciornă nepublicată",
              sub: "Clienții nu văd acest meniu până îl publici.",
            };
  const canPublish = st !== "published" && d.products.length > 0;
  const pubLabel =
    st === "pending"
      ? `Publică (${menu.pendingChanges ?? 0})`
      : st === "published"
        ? "Publicat"
        : "Publică meniul";

  async function after(text: string, undo?: () => Promise<unknown>) {
    await refresh(menu.id);
    toast(
      text,
      undo &&
        (async () => {
          await undo();
          await refresh(menu.id);
        }),
    );
  }

  async function commitPrice(p: ProductDto) {
    if (!priceEdit) return;
    const r = parsePriceInput(priceEdit.value);
    if (!r.ok) return setPriceEdit({ ...priceEdit, err: PRICE_ERROR_MESSAGES[r.error] });
    setPriceEdit(null);
    if (r.minor === p.priceMinor) return;
    try {
      await api.updatePrice(menu.id, p.id, priceEdit.value);
      const pub = publishedById.get(p.id);
      const old = p.priceMinor;
      await after(
        `${p.name}: ${price(r.minor, cur)} salvat în ciornă. Clienții văd încă ${price(pub?.priceMinor ?? old, cur)}.`,
        old === null ? undefined : () => api.updatePrice(menu.id, p.id, formatPriceInput(old)),
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    }
  }

  async function toggleAvailability(p: ProductDto) {
    try {
      const r = await api.setAvailability(p.id, !p.available);
      await after(
        `${p.name}: ${r.available ? "disponibil din nou" : "indisponibil azi"}. Apare imediat în meniul public.`,
        () => api.setAvailability(p.id, r.previous),
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    }
  }

  async function moveCategory(index: number, dir: -1 | 1) {
    const order = d.categories.map((c) => c.id);
    const j = index + dir;
    if (j < 0 || j >= order.length) return;
    const previous = [...order];
    [order[index], order[j]] = [order[j] as string, order[index] as string];
    const moved = order[j] as string;
    await api.reorderCategories(menu.id, order);
    await after("Ordinea categoriilor s-a schimbat în ciornă.", () =>
      api.reorderCategories(menu.id, previous),
    );
    setTimeout(() => document.getElementById(`cat-t-${moved}`)?.focus(), 60);
  }

  /** "Adaugă produs" goes into the category being looked at; with several and none chosen, the editor asks. */
  function addProduct() {
    const only = d.categories.length === 1 ? d.categories[0]?.id : undefined;
    setEditor({ categoryId: catId ?? only ?? "" });
  }

  const productActions = (p: ProductDto): MenuItem[] => [
    { label: "Editează", icon: Pencil, onSelect: () => setEditor({ product: p }) },
    {
      label: "Duplică",
      icon: Copy,
      onSelect: async () => {
        const copy = await api.duplicateProduct(menu.id, p.id);
        await after(`„${copy.name}” adăugat în ciornă.`, () => api.deleteProduct(menu.id, copy.id));
      },
    },
    {
      label: "Elimină din meniu",
      icon: Trash2,
      danger: true,
      onSelect: () => setDialog({ type: "productRemove", product: p }),
    },
  ];

  const productRow = (p: ProductDto) => {
    const isNew = isPublished && sets.added.has(p.id);
    const issues = rowIssues(p, langs);
    // One indicator per weight (admin review): what blocks publishing, what has a consequence, what is optional.
    const chips = [
      ...(issues.publish.length
        ? [
            {
              tone: "warning" as const,
              icon: TriangleAlert,
              label:
                issues.publish.length > 1
                  ? `${issues.publish[0]} +${issues.publish.length - 1}`
                  : (issues.publish[0] ?? ""),
            },
          ]
        : []),
      ...(!p.available ? [CHIPS.unavailable] : []),
      ...(p.frozen ? [{ tone: "info" as const, icon: Snowflake, label: "Congelat" }] : []),
      ...(issues.attention.length
        ? [
            {
              tone: "neutral" as const,
              icon: CircleHelp,
              label:
                issues.attention.length > 1
                  ? `${issues.attention[0]} +${issues.attention.length - 1}`
                  : (issues.attention[0] ?? ""),
            },
          ]
        : []),
      ...(isNew ? [CHIPS.isNew] : sets.changed.has(p.id) ? [CHIPS.changed] : []),
    ];
    const issueText = [
      issues.publish.length ? `Blochează publicarea: ${issues.publish.join(", ")}.` : "",
      issues.attention.length ? `De verificat: ${issues.attention.join(", ")}.` : "",
      issues.optional.length ? `Opțional, lipsește: ${issues.optional.join(", ")}.` : "",
    ]
      .filter(Boolean)
      .join(" ");
    const pub = publishedById.get(p.id);
    const priceChanged = !!pub && !p.variants && !pub.variants && pub.priceMinor !== p.priceMinor;
    const editing = priceEdit?.pid === p.id ? priceEdit : null;
    const shown = p.variants
      ? `${p.variants.map((v) => formatPriceInput(v.priceMinor)).join(" / ")} ${sym}`
      : price(p.priceMinor, cur);
    const meta = p.portion;
    const onPriceKey = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void commitPrice(p);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setPriceEdit(null);
      }
    };
    return (
      <div className="prod-row" key={p.id} id={`prod-${p.id}`}>
        <div className={`thumb${p.imageKey ? " has" : " none"}`} aria-hidden="true">
          {p.imageKey ? (
            <img src={mediaUrl(rid, p.imageKey)} alt="" width={44} height={44} loading="lazy" />
          ) : (
            <ImageIcon {...ICON} />
          )}
        </div>
        <button
          type="button"
          className={`prod-main${p.available ? "" : " off"}`}
          onClick={() => setEditor({ product: p })}
        >
          <span className="pn">{p.name}</span>
          {meta && <span className="pm">{meta}</span>}
          {chips.length > 0 && (
            <span className="row" style={{ gap: 6, marginTop: 3 }} aria-hidden="true">
              {chips.map((c) => (
                <StatusChip key={c.label} {...c} />
              ))}
            </span>
          )}
          {issues.optional.length > 0 && (
            <span className="pm opt" aria-hidden="true">
              Opțional: {issues.optional.join(", ")}
            </span>
          )}
          {issueText && (
            <span className="sr-only">
              {!p.available ? "Indisponibil azi. " : ""}
              {issueText}
            </span>
          )}
        </button>
        <div className="row" style={{ gap: 8, flexWrap: "nowrap", marginLeft: "auto" }}>
          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <div
                className="affix"
                style={{ width: 128, height: 40, borderRadius: 10 }}
                data-invalid={editing.err ? "true" : "false"}
              >
                <input
                  // biome-ignore lint/a11y/noAutofocus: inline edit starts with the field focused (prototype)
                  autoFocus
                  inputMode="decimal"
                  aria-label={`Preț nou pentru ${p.name} în ${currencyWord(cur)}`}
                  aria-invalid={editing.err ? "true" : "false"}
                  value={editing.value}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setPriceEdit({ ...editing, value: e.target.value, err: "" })}
                  onKeyDown={onPriceKey}
                />
                <span>{sym}</span>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <button
                  type="button"
                  className="icon-btn sm"
                  aria-label="Anulează schimbarea prețului"
                  onClick={() => setPriceEdit(null)}
                >
                  <X size={14} strokeWidth={1.75} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="icon-btn sm"
                  style={{ background: "var(--color-primary)", color: "#fff", border: 0 }}
                  aria-label="Salvează prețul în ciornă"
                  onClick={() => commitPrice(p)}
                >
                  <Check size={14} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </div>
              {editing.err && (
                <span
                  role="alert"
                  className="field-error"
                  style={{ maxWidth: 220, fontSize: 12, textAlign: "right" }}
                >
                  {editing.err}
                </span>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="price-btn"
              aria-label={
                p.variants
                  ? `Editează prețurile pentru ${p.name}`
                  : `Schimbă prețul pentru ${p.name}, acum ${price(p.priceMinor, cur)}`
              }
              onClick={() =>
                p.variants
                  ? setEditor({ product: p })
                  : setPriceEdit({
                      pid: p.id,
                      value: p.priceMinor !== null ? formatPriceInput(p.priceMinor) : "",
                      err: "",
                    })
              }
            >
              {priceChanged && <span className="old">{price(pub?.priceMinor ?? null, cur)}</span>}
              <span className="now">{shown}</span>
            </button>
          )}
          <Switch
            checked={p.available}
            label={`${p.name}: disponibil azi`}
            onChange={() => toggleAvailability(p)}
          />
          <PopMenu label={`Acțiuni pentru ${p.name}`} items={productActions(p)} />
        </div>
      </div>
    );
  };

  const blocks = filterBlocks(d.categories, d.products, {
    query,
    filter,
    categoryId: catId,
    langs,
    draftChanged,
  });
  const matches = blocks.reduce((n, b) => n + b.items.length, 0);
  const counts = Object.fromEntries(
    PRODUCT_FILTERS.map(([k]) => [
      k,
      filterBlocks(d.categories, d.products, {
        query: "",
        filter: k,
        categoryId: null,
        langs,
        draftChanged,
      }).reduce((n, b) => n + b.items.length, 0),
    ]),
  ) as Record<ProductFilter, number>;
  const allOpen = blocks.every((b) => open.has(b.category.id));
  const resetView = () => setView({ q: null, cat: null, f: null });

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 8, minWidth: 0 }}>
          <h1 className="h1">Produse și categorii</h1>
          <div className="row" style={{ gap: 10 }}>
            <label htmlFor="menu-pick" className="field-label">
              Meniu
            </label>
            <select
              id="menu-pick"
              className="field-input"
              style={{ width: 240, maxWidth: "100%" }}
              value={menu.id}
              onChange={(e) => {
                setPriceEdit(null);
                setParams({ menu: e.target.value });
              }}
            >
              {active.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.publicVisible ? " · public" : m.publishedVersionId ? " · retras" : " · ciornă"}
                </option>
              ))}
            </select>
            <span className="menu-chip">
              <StatusChip {...menuChip(menu)} large />
            </span>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => setDialog({ type: "category" })}>
            <Plus {...ICON} aria-hidden="true" />
            Categorie
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!d.categories.length}
            onClick={addProduct}
          >
            <Plus {...ICON} aria-hidden="true" />
            Produs
          </button>
        </div>
      </div>

      <Bar tone={bar.tone} icon={bar.icon} title={bar.title} sub={bar.sub}>
        {st === "pending" && (
          <button type="button" className="btn-link" onClick={() => setDialog({ type: "discard" })}>
            Renunță la modificări
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => openPreview(rid, menu.id)}>
          <Eye {...ICON} aria-hidden="true" />
          Previzualizează
        </button>
        {menu.publicVisible && (
          <a
            className="btn btn-secondary btn-sm"
            href={membership.restaurant.publicUrl}
            target="_blank"
            rel="noopener"
          >
            Deschide meniul
          </a>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canPublish}
          onClick={() => setDialog({ type: "publish" })}
        >
          {pubLabel}
        </button>
      </Bar>

      {d.products.length > 0 && (
        <div className="list-tools">
          <div className="list-tools-row">
            <search className="search-field">
              <Search {...ICON} aria-hidden="true" />
              <input
                type="search"
                aria-label="Caută în produsele meniului"
                placeholder="Caută produse…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="icon-btn ghost sm"
                  aria-label="Șterge căutarea"
                  onClick={() => setQuery("")}
                >
                  <X {...ICON} aria-hidden="true" />
                </button>
              )}
            </search>
            <label className="sr-only" htmlFor="cat-pick">
              Categorie
            </label>
            <select
              id="cat-pick"
              className="field-input cat-pick"
              value={catId ?? ""}
              onChange={(e) => {
                const id = e.target.value || null;
                setView({ cat: id });
                if (id) setOpen((s) => new Set(s).add(id));
              }}
            >
              <option value="">Toate categoriile ({d.categories.length})</option>
              {d.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({d.products.filter((p) => p.categoryId === c.id).length})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {d.products.length > 0 && (
        <div className="list-tools-more">
          <div className="list-tools-row">
            <label className="sr-only" htmlFor="prod-filter">
              Arată
            </label>
            <select
              id="prod-filter"
              className="field-input filter-pick"
              value={filter}
              onChange={(e) => setView({ f: e.target.value === "all" ? null : e.target.value })}
            >
              {PRODUCT_FILTERS.map(([k, label]) => (
                <option key={k} value={k}>
                  {label} ({counts[k]})
                </option>
              ))}
            </select>
            {!narrowing && !catId && blocks.length > 1 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setOpen(allOpen ? new Set() : new Set(d.categories.map((c) => c.id)))}
              >
                {allOpen ? "Restrânge toate" : "Extinde toate"}
              </button>
            )}
          </div>
          {(narrowing || catId) && (
            <p aria-live="polite" className="page-sub" style={{ margin: 0 }}>
              {matches
                ? `${plural(matches, "produs afișat", "produse afișate")} din ${d.products.length}.`
                : "Niciun produs nu se potrivește."}{" "}
              <button type="button" className="btn-link" onClick={resetView}>
                Arată toate produsele
              </button>
            </p>
          )}
        </div>
      )}

      {!d.categories.length && (
        <div className="empty-card">
          <span className="t">Începe cu o categorie</span>
          <span className="s">De exemplu „Aperitive” sau „Băuturi”. Apoi adaugi produsele în ea.</span>
          <button type="button" className="btn btn-primary" onClick={() => setDialog({ type: "category" })}>
            Adaugă prima categorie
          </button>
        </div>
      )}

      {d.products.length > 0 && matches === 0 && narrowing && (
        <div className="empty-card">
          <span className="t">Niciun produs găsit</span>
          <span className="s">
            {query.trim()
              ? `Nimic pentru „${query.trim()}” în ${catId ? "categoria aleasă" : "acest meniu"}${filter !== "all" ? " cu filtrul ales" : ""}. Diacriticele nu contează.`
              : "Niciun produs nu se potrivește filtrului ales."}
          </span>
          <button type="button" className="btn btn-secondary" onClick={resetView}>
            Resetează căutarea și filtrele
          </button>
        </div>
      )}

      {blocks.map(({ category: c, index: i, all, items }) => {
        const expanded = narrowing || !!catId || open.has(c.id);
        const blocking = all.filter((p) => rowIssues(p, langs).publish.length).length;
        const noAllergens = all.filter((p) => !p.allergensConfirmed).length;
        const summary = [
          plural(all.length, "produs", "produse"),
          blocking ? `${blocking} blochează publicarea` : "",
          noAllergens ? `${noAllergens} fără alergeni confirmați` : "",
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <section key={c.id} className="cat-block" aria-labelledby={`ch-${c.id}`}>
            <div className="cat-head">
              <h2 id={`ch-${c.id}`} className="cat-title">
                <button
                  type="button"
                  id={`cat-t-${c.id}`}
                  className="cat-toggle"
                  aria-expanded={expanded}
                  aria-controls={`cb-${c.id}`}
                  disabled={narrowing || !!catId}
                  onClick={() =>
                    setOpen((s) => {
                      const n = new Set(s);
                      if (n.has(c.id)) n.delete(c.id);
                      else n.add(c.id);
                      return n;
                    })
                  }
                >
                  <ChevronDown
                    {...ICON}
                    aria-hidden="true"
                    style={{ transform: expanded ? "rotate(180deg)" : undefined, flexShrink: 0 }}
                  />
                  <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                    <span className="cn">{c.name}</span>
                    <span className="cs">{summary}</span>
                  </span>
                </button>
              </h2>
              {isPublished && sets.newCategories.has(c.id) && (
                <StatusChip tone="warning" icon={Plus} label="Nouă" />
              )}
              <PopMenu
                label={`Acțiuni pentru categoria ${c.name}`}
                items={[
                  { label: "Adaugă produs", icon: Plus, onSelect: () => setEditor({ categoryId: c.id }) },
                  ...(i > 0
                    ? [{ label: "Mută mai sus", icon: ArrowUp, onSelect: () => moveCategory(i, -1) }]
                    : []),
                  ...(i < d.categories.length - 1
                    ? [{ label: "Mută mai jos", icon: ArrowDown, onSelect: () => moveCategory(i, 1) }]
                    : []),
                  {
                    label: "Redenumește",
                    icon: Pencil,
                    onSelect: () => setDialog({ type: "category", category: c }),
                  },
                  {
                    label: "Șterge categoria",
                    icon: Trash2,
                    danger: true,
                    onSelect: () => setDialog({ type: "categoryDelete", category: c }),
                  },
                ]}
              />
            </div>
            {expanded && (
              <div id={`cb-${c.id}`} className="stack" style={{ gap: 0 }}>
                {items.map(productRow)}
                {all.length === 0 && <div className="cat-empty">Categoria nu are încă produse.</div>}
                {!narrowing && (
                  <button
                    type="button"
                    className="btn-link"
                    style={{ alignSelf: "flex-start", marginTop: 6 }}
                    onClick={() => setEditor({ categoryId: c.id })}
                  >
                    <Plus {...ICON} aria-hidden="true" />
                    Adaugă produs în {c.name}
                  </button>
                )}
              </div>
            )}
          </section>
        );
      })}

      {sets.removed.length > 0 && (
        <section
          aria-labelledby="rm-h"
          className="cat-block"
          style={{ borderStyle: "dashed", borderColor: "var(--color-border-control)", gap: 4 }}
        >
          <h2 id="rm-h" style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 600 }}>
            Eliminate din ciornă · rămân publice până publici
          </h2>
          {sets.removed.map((r) => (
            <div
              key={r.productId}
              className="row"
              style={{ gap: 12, minHeight: 52, borderTop: "1px solid var(--color-border)" }}
            >
              <span
                style={{
                  flex: "1 1 180px",
                  color: "var(--color-text-muted)",
                  textDecoration: "line-through",
                }}
              >
                {r.subject}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={async () => {
                  await api.restoreProduct(menu.id, r.productId as string);
                  await after(`„${r.subject}” restaurat în ciornă.`);
                }}
              >
                <RefreshCw {...ICON} aria-hidden="true" />
                Restaurează
              </button>
            </div>
          ))}
        </section>
      )}

      {editor && (
        <ProductEditor
          rid={rid}
          menuId={menu.id}
          menuName={menu.name}
          currency={cur}
          categories={d.categories}
          product={editor.product}
          published={editor.product ? publishedById.get(editor.product.id) : undefined}
          initialCategoryId={editor.categoryId}
          askCategory={editor.categoryId === ""}
          onClose={() => setEditor(null)}
          onAvailabilityChanged={() => refresh(menu.id)}
          onSaved={async (saved, previous) => {
            setEditor(null);
            setOpen((s) => new Set(s).add(saved.categoryId));
            await after(
              previous
                ? `Modificările la „${saved.name}” au fost salvate în ciornă. Publici separat.`
                : `Produsul „${saved.name}” a fost adăugat în ciornă. Publici separat.`,
              previous
                ? () => api.updateProduct(menu.id, previous.id, toProductInput(formFromProduct(previous)))
                : () => api.deleteProduct(menu.id, saved.id),
            );
          }}
        />
      )}

      {dialog?.type === "publish" && (
        <PublishDialog
          rid={rid}
          menu={menu}
          publicUrl={membership.restaurant.publicUrl}
          onClose={() => {
            close();
            if (params.get("publish")) setParams({ menu: menu.id });
          }}
          onOpenProduct={(pid) => {
            close();
            const target = d.products.find((x) => x.id === pid);
            if (target) setEditor({ product: target });
          }}
          onPublished={async () => {
            // The restaurant's first time online gets the post-publish screen (QR, link, next steps, spec §1A);
            // later publications keep the prototype toast.
            const firstLive = !(menus.data ?? []).some((m) => m.publicVisible && !m.archived);
            close();
            await refresh(menu.id);
            if (firstLive) {
              navigate("/app/publicat");
              return;
            }
            toast(
              `${menu.name} a fost publicat. Clienții văd noua versiune; linkul și codul QR rămân aceleași.`,
            );
          }}
        />
      )}
      {dialog?.type === "discard" && (
        <DiscardDialog
          rid={rid}
          menu={menu}
          onClose={close}
          onDiscarded={async () => {
            close();
            await refresh(menu.id);
            toast(`Ciorna „${menu.name}” a revenit la versiunea publicată.`);
          }}
        />
      )}
      {dialog?.type === "category" && (
        <CategoryDialog
          category={dialog.category}
          onClose={close}
          onSubmit={async (input) => {
            const old = dialog.category;
            if (old) {
              await api.updateCategory(menu.id, old.id, input);
              close();
              await after("Categoria a fost redenumită în ciornă.", () =>
                api.updateCategory(menu.id, old.id, {
                  name: old.name,
                  translation: { lang: "en", name: old.translations.en?.name ?? "" },
                }),
              );
            } else {
              const { category } = await api.createCategory(menu.id, input);
              close();
              await after(`Categoria „${category.name}” a fost adăugată în ciornă.`, () =>
                api.deleteCategory(menu.id, category.id, { mode: "delete" }),
              );
            }
          }}
        />
      )}
      {dialog?.type === "categoryDelete" && (
        <CategoryDeleteDialog
          category={dialog.category}
          productCount={d.products.filter((p) => p.categoryId === dialog.category.id).length}
          others={d.categories.filter((c) => c.id !== dialog.category.id)}
          onClose={close}
          onConfirm={async (input) => {
            await api.deleteCategory(menu.id, dialog.category.id, input);
            close();
            await after(`Categoria „${dialog.category.name}” a fost ștearsă din ciornă.`);
          }}
        />
      )}
      {dialog?.type === "productRemove" && (
        <ConfirmDialog
          title={`Elimini „${dialog.product.name}” din meniu?`}
          body={
            publishedById.has(dialog.product.id)
              ? "Produsul rămâne în meniul public până publici. Până atunci îl poți restaura."
              : "Produsul nu a fost publicat; va fi eliminat din ciornă."
          }
          confirmLabel="Elimină produsul"
          onClose={close}
          onConfirm={async () => {
            const p = dialog.product;
            await api.deleteProduct(menu.id, p.id);
            close();
            const inPub = publishedById.has(p.id);
            await after(
              inPub
                ? `„${p.name}” eliminat din ciornă. Rămâne public până publici.`
                : `„${p.name}” a fost eliminat din ciornă.`,
              inPub ? () => api.restoreProduct(menu.id, p.id) : undefined,
            );
          }}
        />
      )}
    </>
  );
}

/** Translation languages this menu actually uses (a missing translation is a gap only where there are others). */
function draftLanguages(d: MenuDraftDto): TranslationLang[] {
  return TRANSLATION_LANGS.filter(
    (l) => d.products.some((p) => p.translations[l]) || d.categories.some((c) => c.translations[l]),
  );
}

/**
 * Which categories are open, kept per menu for the browser session (back from the editor or another page, the list
 * looks the same). Big menus start closed: a list of category headings instead of 40,000 px of rows.
 */
function useOpenCategories(menuId: string | null, draft: MenuDraftDto | undefined) {
  const [state, setState] = useState<{ menuId: string | null; open: Set<string> } | null>(null);
  useEffect(() => {
    if (!menuId || !draft || state?.menuId === menuId) return;
    let saved: string[] | null = null;
    try {
      const raw = sessionStorage.getItem(`mnv:open:${menuId}`);
      saved = raw ? (JSON.parse(raw) as string[]) : null;
    } catch {
      saved = null;
    }
    const all = draft.categories.map((c) => c.id);
    setState({
      menuId,
      open: new Set(saved ?? (startsCollapsed(all.length, draft.products.length) ? [] : all)),
    });
  }, [menuId, draft, state?.menuId]);
  const open = state?.menuId === menuId ? state.open : new Set<string>();
  const setOpen = (fn: Set<string> | ((s: Set<string>) => Set<string>)) =>
    setState((prev) => {
      const cur = prev?.menuId === menuId ? prev.open : new Set<string>();
      const next = typeof fn === "function" ? fn(cur) : fn;
      try {
        if (menuId) sessionStorage.setItem(`mnv:open:${menuId}`, JSON.stringify([...next]));
      } catch {
        // private mode: the list simply forgets on reload
      }
      return { menuId, open: next };
    });
  return [open, setOpen] as const;
}
